/**
 * CopyEditMode: the admin's in-app copy editor.
 *
 * Renders nothing unless the signed-in account is an admin AND the main
 * menu's "Edit copy" switch is on. Then:
 *   - a pill at the top says the page is in edit mode, with Done;
 *   - every click is caught in the capture phase (before React's root
 *     listener) so a tap on a link or button does not navigate, and the text
 *     under the finger opens the editor sheet;
 *   - the sheet edits each text segment of the tapped element, or hides the
 *     element / its section, scoped to this page or every page;
 *   - saves go to PUT/DELETE /api/admin/copy-overrides and are applied to the
 *     live DOM at once (lib/copyOverrides), so what you see is what shipped.
 *
 * Everything inside the sheet carries data-nx-copy-editor so the engine never
 * rewrites the editor's own labels, and clicks inside it pass through.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { X, EyeOff, RotateCcw, Check, Layers } from "lucide-react";
import { useAuthOptional } from "@/components/AuthProvider";
import { useCopyEditActive, setCopyEditActive } from "@/lib/copyEditMode";
import {
  COPY_EDITOR_ATTR,
  COPY_OVERRIDES_CHANGED_EVENT,
  currentRouteKey,
  findEditableTextAt,
  listCopyOverrides,
  lookupCopyOverride,
  normalizeCopyText,
  originalTextOf,
  removeCopyOverride,
  sectionFor,
  upsertCopyOverride,
  type CopyAction,
  type CopyOverride,
  type EditableText,
} from "@/lib/copyOverrides";

const ADMIN_URL = "/api/admin/copy-overrides";

interface Segment {
  original: string;
  draft: string;
  existing: CopyOverride | undefined;
}

function segmentsFor(target: EditableText, route: string): Segment[] {
  return target.nodes.map((t) => {
    const original = normalizeCopyText(originalTextOf(t));
    const existing = lookupCopyOverride(route, original);
    return {
      original,
      draft: existing?.action === "replace" ? existing.value ?? original : original,
      existing,
    };
  });
}

export function CopyEditMode() {
  const auth = useAuthOptional();
  const isAdmin = auth?.account?.isAdmin === true;
  const active = useCopyEditActive();
  const enabled = isAdmin && active;

  const [target, setTarget] = useState<EditableText | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [scope, setScope] = useState<"page" | "all">("page");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listTick, setListTick] = useState(0);
  const [showList, setShowList] = useState(false);

  // An admin who signs out mid-edit leaves edit mode with them.
  useEffect(() => {
    if (!isAdmin && active) setCopyEditActive(false);
  }, [isAdmin, active]);

  useEffect(() => {
    const bump = () => setListTick((n) => n + 1);
    window.addEventListener(COPY_OVERRIDES_CHANGED_EVENT, bump);
    return () => window.removeEventListener(COPY_OVERRIDES_CHANGED_EVENT, bump);
  }, []);

  const close = useCallback(() => {
    setTarget(null);
    setSegments([]);
    setError(null);
  }, []);

  // Capture-phase click catcher: runs before React's root listener, so the
  // tapped control never fires. Clicks inside the editor pass through.
  useEffect(() => {
    if (!enabled) return;
    const onClick = (e: MouseEvent) => {
      const el = e.target as Element | null;
      if (el?.closest?.(`[${COPY_EDITOR_ATTR}]`)) return;
      e.preventDefault();
      e.stopPropagation();
      const found = findEditableTextAt(e.clientX, e.clientY);
      if (!found) return;
      const route = currentRouteKey();
      const segs = segmentsFor(found, route);
      const existingScope = segs.find((s) => s.existing)?.existing?.route;
      setScope(existingScope === "*" ? "all" : "page");
      setTarget(found);
      setSegments(segs);
      setError(null);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [enabled]);

  const route = useMemo(() => (scope === "all" ? "*" : currentRouteKey()), [scope, target]);
  const pageRoute = currentRouteKey();
  const pageOverrides = useMemo(() => listCopyOverrides(pageRoute), [pageRoute, listTick, enabled]);

  const send = useCallback(
    async (method: "PUT" | "DELETE", body: object) => {
      if (!auth?.authedFetch) throw new Error("Not signed in");
      const res = await auth.authedFetch(ADMIN_URL, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `${res.status}`;
        try {
          const j = (await res.json()) as { error?: string; message?: string };
          msg = j.error ?? j.message ?? msg;
        } catch {
          /* keep the status */
        }
        throw new Error(msg);
      }
      return res;
    },
    [auth],
  );

  const run = useCallback(
    async (fn: () => Promise<void>) => {
      setBusy(true);
      setError(null);
      try {
        await fn();
        close();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Save failed");
      } finally {
        setBusy(false);
      }
    },
    [close],
  );

  const save = () =>
    run(async () => {
      for (const seg of segments) {
        const value = seg.draft.trim();
        if (!value || value === seg.original) {
          // Back to the bundled text: drop any override on either scope.
          for (const r of [pageRoute, "*"]) {
            if (lookupCopyOverride(r, seg.original)?.route === r) {
              await send("DELETE", { route: r, original: seg.original });
              removeCopyOverride(r, seg.original);
            }
          }
          continue;
        }
        // Moving scope: clear the other scope's row first.
        const other = route === "*" ? pageRoute : "*";
        if (lookupCopyOverride(other, seg.original)?.route === other) {
          await send("DELETE", { route: other, original: seg.original });
          removeCopyOverride(other, seg.original);
        }
        const o: CopyOverride = { route, original: seg.original, action: "replace", value };
        await send("PUT", o);
        upsertCopyOverride(o);
      }
    });

  const hide = (action: Exclude<CopyAction, "replace">) =>
    run(async () => {
      const seg = segments[0];
      if (!seg) return;
      const o: CopyOverride = { route, original: seg.original, action, value: null };
      await send("PUT", o);
      upsertCopyOverride(o);
    });

  const reset = () =>
    run(async () => {
      for (const seg of segments) {
        for (const r of [pageRoute, "*"]) {
          if (lookupCopyOverride(r, seg.original)?.route === r) {
            await send("DELETE", { route: r, original: seg.original });
            removeCopyOverride(r, seg.original);
          }
        }
      }
    });

  const removeListed = (o: CopyOverride) =>
    run(async () => {
      await send("DELETE", { route: o.route, original: o.original });
      removeCopyOverride(o.route, o.original);
    });

  if (!enabled) return null;

  const hasOverride = segments.some((s) => s.existing);
  const sectionTag = target ? sectionFor(target.element).tagName.toLowerCase() : "";

  return (
    <div {...{ [COPY_EDITOR_ATTR]: "" }} data-testid="copy-edit-mode">
      {/* Mode pill */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-[1200] flex items-center gap-2 rounded-full px-3 py-1.5 shadow-lg"
        style={{
          top: "calc(var(--nx-safe-top) + 8px)",
          background: "hsl(0 80% 45%)",
          color: "#fff",
        }}
      >
        <span className="text-[11px] font-black uppercase tracking-[0.12em]">Editing copy: tap any text</span>
        <button
          type="button"
          data-testid="copy-edit-list"
          onClick={() => setShowList((v) => !v)}
          className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-bold"
        >
          {pageOverrides.length} here
        </button>
        <button
          type="button"
          data-testid="copy-edit-done"
          onClick={() => {
            close();
            setCopyEditActive(false);
          }}
          className="rounded-full bg-white px-2.5 py-0.5 text-[11px] font-black"
          style={{ color: "hsl(0 80% 40%)" }}
        >
          Done
        </button>
      </div>

      {/* Overrides on this page */}
      {showList && !target && (
        <div
          className="fixed inset-x-3 z-[1200] rounded-2xl p-3 shadow-2xl surface-elev max-h-[50dvh] overflow-y-auto"
          style={{ top: "calc(var(--nx-safe-top) + 52px)" }}
        >
          <p className="text-[11px] font-black uppercase tracking-[0.12em] mb-2" style={{ color: "var(--text-ink-faint)" }}>
            Overrides on this page
          </p>
          {pageOverrides.length === 0 && (
            <p className="text-[11px] text-muted-foreground">None yet. Tap any text to change or hide it.</p>
          )}
          {pageOverrides.map((o) => (
            <div key={`${o.route}|${o.original}`} className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] truncate" style={{ color: "var(--text-secondary)" }}>{o.original}</p>
                <p className="text-[12px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                  {o.action === "replace" ? o.value : o.action === "hide" ? "Hidden" : "Section hidden"}
                  {o.route === "*" && <span className="ml-1 text-[10px] text-muted-foreground">(all pages)</span>}
                </p>
              </div>
              <button
                type="button"
                aria-label="Remove override"
                disabled={busy}
                onClick={() => removeListed(o)}
                className="shrink-0 rounded-full p-1"
                style={{ color: "var(--text-secondary)" }}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Editor sheet */}
      {target && (
        <>
          <div className="fixed inset-0 z-[1190] bg-black/40" onClick={close} aria-hidden />
          <div
            role="dialog"
            aria-label="Edit copy"
            data-testid="copy-edit-sheet"
            className="fixed inset-x-0 bottom-0 z-[1200] rounded-t-3xl p-4 shadow-2xl surface-elev max-h-[80dvh] overflow-y-auto"
            style={{ paddingBottom: "calc(var(--nx-safe-bottom) + 16px)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-[12px] font-black uppercase tracking-[0.14em]" style={{ color: "var(--text-ink-faint)" }}>
                Edit copy
              </p>
              <button type="button" aria-label="Close" onClick={close} className="rounded-full p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            {segments.map((seg, i) => (
              <div key={i} className="mb-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Original</p>
                <p className="text-[12px] mb-2 whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{seg.original}</p>
                <textarea
                  data-testid={`copy-edit-text-${i}`}
                  value={seg.draft}
                  rows={Math.min(6, Math.max(2, Math.ceil(seg.draft.length / 40)))}
                  onChange={(e) =>
                    setSegments((prev) => prev.map((s, j) => (j === i ? { ...s, draft: e.target.value } : s)))
                  }
                  className="w-full rounded-xl border border-border bg-background p-2 text-[14px]"
                  style={{ color: "var(--text-primary)" }}
                />
              </div>
            ))}

            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Applies to</span>
              {(["page", "all"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`copy-edit-scope-${s}`}
                  onClick={() => setScope(s)}
                  className="rounded-full px-2.5 py-1 text-[11px] font-bold border"
                  style={{
                    borderColor: scope === s ? "var(--accent-fill)" : "var(--border)",
                    color: scope === s ? "var(--accent-fill)" : "var(--text-secondary)",
                  }}
                >
                  {s === "page" ? `This page (${pageRoute})` : "All pages"}
                </button>
              ))}
            </div>

            {error && (
              <p className="text-[11px] mb-2" style={{ color: "hsl(0 80% 55%)" }} data-testid="copy-edit-error">
                {error}
              </p>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="copy-edit-save"
                disabled={busy}
                onClick={save}
                className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-[13px] font-black"
                style={{ background: "var(--accent-fill)", color: "#0a0a0f" }}
              >
                <Check className="w-4 h-4" /> Save
              </button>
              <button
                type="button"
                data-testid="copy-edit-hide"
                disabled={busy}
                onClick={() => hide("hide")}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl py-2 text-[12px] font-bold border border-border"
                style={{ color: "var(--text-primary)" }}
              >
                <EyeOff className="w-4 h-4" /> Hide this text
              </button>
              <button
                type="button"
                data-testid="copy-edit-hide-section"
                disabled={busy}
                onClick={() => hide("hide_section")}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl py-2 text-[12px] font-bold border border-border"
                style={{ color: "var(--text-primary)" }}
              >
                <Layers className="w-4 h-4" /> Hide section ({sectionTag})
              </button>
              {hasOverride && (
                <button
                  type="button"
                  data-testid="copy-edit-reset"
                  disabled={busy}
                  onClick={reset}
                  className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-xl py-2 text-[12px] font-bold"
                  style={{ color: "var(--text-secondary)" }}
                >
                  <RotateCcw className="w-4 h-4" /> Back to the original
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
